'use client';

import { useRef, useEffect, useState } from 'react';
import { AnimationContainer } from '@/components/ui/animation-container';
import { MagicBadge } from '@/components/ui/magic-badge';
import { Button } from '@/components/ui/button';
import Link from '@/components/i18n/LocaleLink';
import { CheckCircle, Coins, CreditCard } from 'lucide-react';
import { Lang, TranslationKeys, Plan } from '../types';
import { isContactSalesPlan } from '@/lib/pricing/plan-contact-sales';
import { formatPlanPriceUsd } from '@/lib/pricing/format-plan-price';

/**
 * Decide whether a plan belongs to the "For teams" audience.
 * Matches by slug ('team', 'business', 'enterprise') or by name
 * containing the words, so it works for both the DB-backed plans
 * and the fallback static list.
 */
function isTeamPlan(plan: Plan): boolean {
  const slug = (plan.slug || '').toLowerCase();
  const name = (plan.name || '').toLowerCase();
  const teamKeywords = ['team', 'business', 'enterprise'];
  return teamKeywords.some((k) => slug.includes(k) || name.includes(k));
}

type PlanCopy = Pick<Plan, 'name' | 'description' | 'benefits' | 'buttonText'>;

const FALLBACK_PLAN_COPY: Record<Lang, Record<string, PlanCopy>> = {
  en: {
    free: { name: 'Free', description: 'Try EZDocuAI', benefits: ['5 pages/month. Resets every cycle.', 'Additional: $2.99/page.', 'High-accuracy OCR', 'Format preservation', 'AI translation', 'Synchronized dual-panel editor', 'PDF and DOCX export', 'File retention: 7 days'], buttonText: 'Start for free' },
    pro: { name: 'Pro', description: 'For the professional translator', benefits: ['25 tokens included, accumulate every month.', 'Additional: $1.99/token.', 'Everything in Free, plus:', 'Embedded translation certificate', '"Human Verified" seal with QR', 'Manuscripts', 'Up to 3 translation glossaries', 'Productivity dashboard', 'Configurable retention', 'History: 90 days'], buttonText: 'Start with Pro' },
    max: { name: 'Max', description: 'To grow your operation', benefits: ['100 tokens included, accumulate every month.', 'Additional: $1.49/token.', 'Everything in Pro, plus:', '2 members included (+$39/month additional seat)', 'Post-edit assistant (Coming soon)', 'Workflows', 'Smart task routing (Coming soon)', 'Actionable brackets', 'Retention up to 90 days', 'History: 1 year'], buttonText: 'Start with Max' },
    team: { name: 'Team', description: 'For translation offices', benefits: ['400 tokens for 2 to 20 members. Accumulated.', 'Additional: $1.25/token.', 'Everything in Max, plus:', 'Admin panel', 'Automatic assignment by language', 'Supervisor role (Coming soon)', 'Throughput metrics', 'Consistency auditor', 'Exportable reports', 'Priority support', 'History: 2 years'], buttonText: 'Start with Team' },
    business: { name: 'Business', description: 'For large organizations', benefits: ['1,000 credits for 21 to 50 members. They accumulate each month.', 'Additional: $0.99/credit.', 'Everything in Team, plus:', 'Assisted onboarding for the team'], buttonText: 'Start with Business' },
    enterprise: { name: 'Enterprise', description: 'Dedicated infrastructure', benefits: ['+20 members. Negotiated volume and SLA.', 'Everything in Team, plus:', 'SSO / SAML', 'API access', 'Guaranteed SLA', 'Custom retention (up to 10 years)', 'White-label client portal', 'Full audit trail', 'Dedicated account manager', 'NET 30 billing', 'Assisted onboarding'], buttonText: 'Contact sales' },
  },
  es: {
    free: { name: 'Gratis', description: 'Prueba EZDocuAI', benefits: ['5 páginas gratis/mes. Se reinicia cada ciclo.', 'Adicionales: $2.99/página.', 'OCR de alta precisión', 'Preservación de formato', 'Traducción con IA', 'Editor doble panel sincronizado', 'Exportación PDF y DOCX', 'Retención fija: 7 días'], buttonText: 'Empezar gratis' },
    pro: { name: 'Pro', description: 'Para el traductor profesional', benefits: ['25 tokens incluidos, se acumulan cada mes.', 'Adicionales: $1.99/token.', 'Todo lo del Gratis, más:', 'Certificado de traducción embebido', 'Sello "Human Verified" con QR', 'Manuscritos', 'Hasta 3 glosarios de traducción', 'Panel de productividad', 'Retención configurable', 'Historial: 90 días'], buttonText: 'Empezar con Pro' },
    max: { name: 'Max', description: 'Para crecer tu operación', benefits: ['100 tokens incluidos, se acumulan cada mes.', 'Adicionales: $1.49/token.', 'Todo lo del Pro, más:', 'Hasta 2 miembros (+$39/mes por miembro adicional)', 'Asistente de post-edición (Próximamente)', 'Flujos de trabajo', 'Enrutamiento inteligente de tareas (Próximamente)', 'Brackets accionables', 'Retención hasta 90 días', 'Historial: 1 año'], buttonText: 'Empezar con Max' },
    team: { name: 'Team', description: 'Para oficinas de traducción', benefits: ['400 tokens para 2 a 20 miembros. Se acumulan.', 'Adicionales: $1.25/token.', 'Todo lo del Max, más:', 'Panel de administrador', 'Asignación automática por idioma', 'Rol de supervisor (Próximamente)', 'Métricas de productividad', 'Auditor de consistencia', 'Reportes exportables', 'Soporte prioritario', 'Historial: 2 años'], buttonText: 'Empezar con Team' },
    business: { name: 'Business', description: 'Para grandes organizaciones', benefits: ['1.000 créditos para 21 a 50 miembros. Se acumulan cada mes.', 'Adicionales: $0.99/crédito.', 'Todo lo del Team, más:', 'Onboarding asistido para el equipo'], buttonText: 'Empezar con Business' },
    enterprise: { name: 'Enterprise', description: 'Infraestructura dedicada', benefits: ['+20 miembros. Volumen y SLA negociados.', 'Todo lo del Team, más:', 'SSO / SAML', 'Acceso API', 'SLA garantizado', 'Retención personalizada (hasta 10 años)', 'Portal de cliente white-label', 'Auditoría completa', 'Account manager dedicado', 'Facturación NET 30', 'Onboarding asistido'], buttonText: 'Contactar ventas' },
  },
  pt: {
    free: { name: 'Free', description: 'Experimente o EZDocuAI', benefits: ['5 páginas/mês. Reinicia a cada ciclo.', 'Adicionais: $2.99/página.', 'OCR de alta precisão', 'Preservação de formato', 'Tradução com IA', 'Editor de painel duplo sincronizado', 'Exportação em PDF e DOCX', 'Retenção de arquivos: 7 dias'], buttonText: 'Começar grátis' },
    pro: { name: 'Pro', description: 'Para o tradutor profissional', benefits: ['25 tokens incluídos, acumulam a cada mês.', 'Adicionais: $1.99/token.', 'Tudo do Free, mais:', 'Certificado de tradução incorporado', 'Selo "Human Verified" com QR', 'Manuscritos', 'Até 3 glossários de tradução', 'Painel de produtividade', 'Retenção configurável', 'Histórico: 90 dias'], buttonText: 'Começar com Pro' },
    max: { name: 'Max', description: 'Para expandir sua operação', benefits: ['100 tokens incluídos, acumulam a cada mês.', 'Adicionais: $1.49/token.', 'Tudo do Pro, mais:', '2 membros incluídos (+$39/mês por usuário adicional)', 'Assistente de pós-edição (Em breve)', 'Fluxos de trabalho', 'Divisão inteligente de tarefas (Em breve)', 'Brackets acionáveis', 'Retenção até 90 dias', 'Histórico: 1 ano'], buttonText: 'Começar com Max' },
    team: { name: 'Team', description: 'Para escritórios de tradução', benefits: ['400 tokens para 2 a 20 membros. Acumulados.', 'Adicionais: $1.25/token.', 'Tudo do Max, mais:', 'Painel administrativo', 'Atribuição automática por idioma', 'Função de supervisor (Em breve)', 'Métricas de produtividade', 'Auditor de consistência', 'Relatórios exportáveis', 'Suporte prioritário', 'Histórico: 2 anos'], buttonText: 'Começar com Team' },
    business: { name: 'Business', description: 'Para grandes organizações', benefits: ['1.000 créditos para 21 a 50 membros. Acumulam a cada mês.', 'Adicionais: $0.99/crédito.', 'Tudo do Team, mais:', 'Onboarding assistido para a equipe'], buttonText: 'Começar com Business' },
    enterprise: { name: 'Enterprise', description: 'Infraestrutura dedicada', benefits: ['+20 membros. Volume e SLA negociados.', 'Tudo do Team, mais:', 'SSO / SAML', 'Acesso à API', 'SLA garantido', 'Retenção personalizada (até 10 anos)', 'Portal do cliente white-label', 'Trilha de auditoria completa', 'Account manager dedicado', 'Faturamento NET 30', 'Onboarding assistido'], buttonText: 'Falar com vendas' },
  },
  de: {
    free: { name: 'Free', description: 'EZDocuAI ausprobieren', benefits: ['5 Seiten/Monat. Zurücksetzung in jedem Zyklus.', 'Zusätzlich: $2.99/Seite.', 'Hochpräzise OCR', 'Formaterhaltung', 'KI-Übersetzung', 'Synchronisierter Zwei-Fenster-Editor', 'PDF- und DOCX-Export', 'Dateiaufbewahrung: 7 Tage'], buttonText: 'Kostenlos starten' },
    pro: { name: 'Pro', description: 'Für den professionellen Übersetzer', benefits: ['25 Token enthalten, kumulieren jeden Monat.', 'Zusätzlich: $1.99/Token.', 'Alles aus Free, zusätzlich:', 'Eingebettetes Übersetzungszertifikat', '„Human Verified“-Siegel mit QR', 'Manuskripte', 'Bis zu 3 Übersetzungsglossare', 'Produktivitäts-Dashboard', 'Konfigurierbare Aufbewahrung', 'Verlauf: 90 Tage'], buttonText: 'Mit Pro starten' },
    max: { name: 'Max', description: 'Um Ihre Arbeit zu skalieren', benefits: ['100 Token enthalten, kumulieren jeden Monat.', 'Zusätzlich: $1.49/Token.', 'Alles aus Pro, zusätzlich:', '2 Mitglieder enthalten (+$39/Monat pro zusätzlichem Platz)', 'Post-Edit-Assistent (Demnächst)', 'Arbeitsabläufe', 'Intelligente Aufgabenverteilung (Demnächst)', 'Aktionsfähige Klammern', 'Aufbewahrung bis zu 90 Tagen', 'Verlauf: 1 Jahr'], buttonText: 'Mit Max starten' },
    team: { name: 'Team', description: 'Für Übersetzungsbüros', benefits: ['400 Token für 2 bis 20 Mitglieder. Kumuliert.', 'Zusätzlich: $1.25/Token.', 'Alles aus Max, zusätzlich:', 'Admin-Panel', 'Automatische Zuweisung nach Sprache', 'Supervisor-Rolle (Demnächst)', 'Durchsatzmetriken', 'Konsistenzprüfer', 'Exportierbare Berichte', 'Prioritäts-Support', 'Verlauf: 2 Jahre'], buttonText: 'Mit Team starten' },
    business: { name: 'Business', description: 'Für große Organisationen', benefits: ['1.000 Credits für 21 bis 50 Mitglieder. Sie sammeln sich jeden Monat.', 'Zusätzlich: $0.99/Credit.', 'Alles aus Team, plus:', 'Begleitetes Onboarding für das Team'], buttonText: 'Mit Business starten' },
    enterprise: { name: 'Enterprise', description: 'Dedizierte Infrastruktur', benefits: ['+20 Mitglieder. Volumen und SLA verhandelbar.', 'Alles aus Team, plus:', 'SSO / SAML', 'API-Zugang', 'Garantiertes SLA', 'Individuelle Aufbewahrung (bis 10 Jahre)', 'White-Label-Kundenportal', 'Vollständiger Audit-Trail', 'Dedizierter Account Manager', 'NET-30-Abrechnung', 'Begleitetes Onboarding'], buttonText: 'Vertrieb kontaktieren' },
  },
  fr: {
    free: { name: 'Free', description: 'Essayer EZDocuAI', benefits: ['5 pages/mois. Réinitialisé à chaque cycle.', 'Supplémentaires : 2,99 $/page.', 'OCR haute précision', 'Préservation du format', 'Traduction IA', 'Éditeur double panneau synchronisé', 'Export PDF et DOCX', 'Rétention des fichiers : 7 jours'], buttonText: 'Commencer gratuitement' },
    pro: { name: 'Pro', description: 'Pour le traducteur professionnel', benefits: ['25 tokens inclus, cumulables chaque mois.', 'Supplémentaires : 1,99 $/token.', 'Tout de Free, plus :', 'Certificat de traduction intégré', 'Sceau « Human Verified » avec QR', 'Manuscrits', 'Jusqu’à 3 glossaires de traduction', 'Tableau de bord de productivité', 'Rétention configurable', 'Historique : 90 jours'], buttonText: 'Commencer avec Pro' },
    max: { name: 'Max', description: 'Pour développer votre activité', benefits: ['100 tokens inclus, cumulables chaque mois.', 'Supplémentaires : 1,49 $/token.', 'Tout de Pro, plus :', '2 membres inclus (+39 $/mois par siège supplémentaire)', 'Assistant de post-édition (Bientôt disponible)', 'Flux de travail', 'Répartition intelligente des tâches (Bientôt disponible)', 'Brackets actionnables', 'Rétention jusqu’à 90 jours', 'Historique : 1 an'], buttonText: 'Commencer avec Max' },
    team: { name: 'Team', description: 'Pour les bureaux de traduction', benefits: ['400 tokens pour 2 à 20 membres. Cumulés.', 'Supplémentaires : 1,25 $/token.', 'Tout de Max, plus :', 'Panneau d’administration', 'Attribution automatique par langue', 'Rôle de superviseur (Bientôt disponible)', 'Métriques de débit', 'Auditeur de cohérence', 'Rapports exportables', 'Support prioritaire', 'Historique : 2 ans'], buttonText: 'Commencer avec Team' },
    business: { name: 'Business', description: 'Pour les grandes organisations', benefits: ['1 000 crédits pour 21 à 50 membres. Ils s’accumulent chaque mois.', 'Supplémentaires : 0,99 $/crédit.', 'Tout le Team, plus :', 'Onboarding assisté pour l’équipe'], buttonText: 'Commencer avec Business' },
    enterprise: { name: 'Enterprise', description: 'Infrastructure dédiée', benefits: ['+20 membres. Volume et SLA négociés.', 'Tout le Team, plus :', 'SSO / SAML', 'Accès API', 'SLA garanti', 'Rétention personnalisée (jusqu’à 10 ans)', 'Portail client white-label', 'Journal d’audit complet', 'Account manager dédié', 'Facturation NET 30', 'Onboarding assisté'], buttonText: 'Contacter les ventes' },
  },
};

/** Lightweight viewport-triggered fade-in card (replaces framer-motion) */
function FadeInCard({ index, className, children }: { index: number; className?: string; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.1, rootMargin: '50px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
        transition: `opacity 0.5s ease-out ${index * 0.15}s, transform 0.5s ease-out ${index * 0.15}s`,
      }}
    >
      {children}
    </div>
  );
}

export default function PricingSection({
  t,
  plans,
  isYearly,
  setIsYearly,
  planStartIndex,
  setPlanStartIndex,
  copy,
  lang = 'es',
}: {
  t: TranslationKeys;
  plans: Plan[];
  isYearly: boolean;
  setIsYearly: (v: boolean) => void;
  planStartIndex: number;
  setPlanStartIndex: (v: number) => void;
  copy?: import('@/lib/marketing/site-copy').SiteCopy;
  lang?: string;
}) {
  const { copyOr } = require('@/lib/marketing/site-copy') as typeof import('@/lib/marketing/site-copy');
  const title = copyOr(copy, 'home.pricing.title', t.pricingTitle);
  const subtitle = copyOr(copy, 'home.pricing.subtitle', t.pricingDesc);
  // Audience toggle: "For individuals" shows personal plans, "For teams"
  // shows team/business plans. Replaces the old prev/next chevrons that
  // made the Team plan hard to discover.
  const [audience, setAudience] = useState<'individuals' | 'teams'>('individuals');
  return (
    <section id="pricing" className="py-6 px-4 bg-gradient-to-b from-white via-purple-50/50 to-white">
      <div className="max-w-7xl mx-auto">
        <AnimationContainer delay={0.1}>
          <div className="flex flex-col items-center justify-center w-full py-2 max-w-xl mx-auto">
            <MagicBadge title={t.pricing} />
            <h2 className="text-center text-3xl md:text-5xl !leading-[1.1] font-bold text-gray-900 mt-6">
              {title}
            </h2>
            <p className="mt-4 text-center text-lg text-gray-600 max-w-lg">
              {subtitle}
            </p>

            {/* Audience toggle: Individuals | Teams */}
            <div className="flex items-center gap-1 mt-8 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setAudience('individuals')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer ${
                  audience === 'individuals'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.pricingToggleIndividuals}
              </button>
              <button
                onClick={() => setAudience('teams')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer ${
                  audience === 'teams'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.pricingToggleTeams}
              </button>
            </div>

            {/* Monthly/Yearly Toggle */}
            <div className="flex items-center gap-1 mt-4 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setIsYearly(false)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer ${
                  !isYearly
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.monthly}
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer ${
                  isYearly
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.yearly}
              </button>
            </div>
          </div>
        </AnimationContainer>

        {(() => {
            // Fallback reflects the 5 real plans seeded in DB: Free, Pro, Max, Team, Enterprise.
            const fallbackPlans = [
              { id: 1, slug: 'free', name: 'Free', description: 'Prueba EZDocuAI', monthlyPrice: '0', yearlyPrice: '0', yearlyDiscount: 0, benefits: ['5 páginas gratis/mes. Se reinicia cada ciclo.', 'Adicionales: $2.99/página.', 'OCR de alta precisión', 'Preservación de formato', 'Traducción con IA', 'Editor doble panel sincronizado', 'Export PDF y DOCX', 'Retención fija: 7 días'], creditsMonthly: 5, creditsYearly: 5, pagesPerMonth: 5, isPopular: false, buttonText: 'Empezar gratis', buttonVariant: 'outline' },
              { id: 2, slug: 'pro', name: 'Pro', description: 'Para el traductor profesional', monthlyPrice: '49', yearlyPrice: '490', yearlyDiscount: 17, benefits: ['25 tokens incluidos, se acumulan cada mes.', 'Adicionales: $1.99/token.', 'Todo lo del Free, más:', 'Certificado de traducción embebido', 'Sello "Human Verified" con QR', 'Manuscritos', 'Hasta 3 corpus de traducción', 'Modo Confidencial (Nuevo)', 'Dashboard de privacidad', 'Retención configurable', 'Historial: 90 días', 'Portal cliente (Pronto)'], creditsMonthly: 25, creditsYearly: 300, isPopular: true, buttonText: 'Empezar con Pro', buttonVariant: 'primary' },
              { id: 3, slug: 'max', name: 'Max', description: 'Para crecer tu operación', monthlyPrice: '149', yearlyPrice: '1490', yearlyDiscount: 17, benefits: ['100 tokens incluidos, se acumulan cada mes.', 'Adicionales: $1.49/token.', 'Todo lo del Pro, más:', 'Hasta 2 miembros (+$39/mes c/u extra)', 'Asistente de post-edición (Nuevo)', 'División de trabajo', 'Corpus ilimitados', 'Verificación con OTP', 'Brackets automáticos', 'Retención hasta 90 días', 'Historial: 1 año'], creditsMonthly: 100, creditsYearly: 1200, isPopular: false, buttonText: 'Empezar con Max', buttonVariant: 'outline' },
              { id: 4, slug: 'team', name: 'Team', description: 'Para oficinas de traducción', monthlyPrice: '499', yearlyPrice: '4990', yearlyDiscount: 17, benefits: ['400 tokens para 2 a 20 miembros. Se acumulan.', 'Adicionales: $1.25/token.', 'Todo lo del Max, más:', 'Panel de administrador', 'Asignación automática por idioma', 'Rol de supervisor', 'Métricas de throughput', 'Auditor de consistencia', 'Reportes exportables', 'Soporte prioritario', 'Historial: 2 años'], creditsMonthly: 400, creditsYearly: 4800, isPopular: false, buttonText: 'Empezar con Team', buttonVariant: 'outline' },
              { id: 5, slug: 'enterprise', name: 'Enterprise', description: 'Infraestructura dedicada', monthlyPrice: '0', yearlyPrice: '0', yearlyDiscount: 0, benefits: ['+20 miembros. Volumen y SLA negociados.', 'Todo lo del Team, más:', 'SSO / SAML', 'API access', 'SLA garantizado', 'Retención custom (hasta 10 años)', 'White-label client portal', 'Audit trail completo', 'Account manager dedicado', 'Facturación NET 30', 'Onboarding asistido'], creditsMonthly: 0, creditsYearly: 0, isPopular: false, buttonText: 'Contactar ventas', buttonVariant: 'outline', contactSales: true, contactSalesUrl: 'mailto:sales@ezdocu.ai' },
            ] as Plan[];
            // When the plans API is down/empty we render the static
            // `fallbackPlans` array above, which only has Spanish copy —
            // for that outage path, hardcoded FALLBACK_PLAN_COPY must fully
            // localize it (there's no real per-locale data to prefer).
            const isUsingStaticFallback = plans.length === 0;
            const plansToDisplay = isUsingStaticFallback ? fallbackPlans : plans;
            // Otherwise, hardcoded copy is a true fallback: it only fills in
            // fields the API/DB didn't provide for this locale. It must
            // never clobber live (admin-edited, DB-translated) content, or
            // plan edits and per-locale translations stop showing up.
            const allPlans = plansToDisplay.map((plan) => {
              const fallback = FALLBACK_PLAN_COPY[lang as Lang]?.[plan.slug];
              if (!fallback) return plan;
              if (isUsingStaticFallback) return { ...plan, ...fallback };
              return {
                ...plan,
                name: plan.name || fallback.name,
                description: plan.description || fallback.description,
                benefits: plan.benefits && plan.benefits.length > 0 ? plan.benefits : fallback.benefits,
                buttonText: plan.buttonText || fallback.buttonText,
              };
            });

            // Filter by audience: individuals get personal plans; teams get
            // team/business/enterprise plans.
            const filtered = allPlans.filter((p) =>
              audience === 'teams' ? isTeamPlan(p) : !isTeamPlan(p),
            );
            const visiblePlans = filtered.length > 0 ? filtered : allPlans;
            // Use setPlanStartIndex to no-op writes so the parent prop stays
            // stable — we no longer expose the left/right chevrons.
            void planStartIndex; void setPlanStartIndex;

            // Responsive grid: keep 3-col max so 4 team plans wrap cleanly.
            const gridCols = visiblePlans.length === 1
              ? 'md:grid-cols-1 md:max-w-md md:mx-auto'
              : visiblePlans.length === 2
                ? 'md:grid-cols-2 md:max-w-3xl md:mx-auto'
                : 'md:grid-cols-3';

            return (
              <div className="relative py-4">
                <div className={`grid ${gridCols} gap-6`}>
                  {/* Dynamic Plans from Database */}
                  {visiblePlans.map((plan, index) => {
                    const price = isYearly ? Math.round(parseFloat(plan.yearlyPrice)) : Math.round(parseFloat(plan.monthlyPrice));
                    const isPopular = plan.isPopular;
                    const isPrimary = plan.buttonVariant === 'primary';
                    const isContactSales = isContactSalesPlan(plan);
                    const salesUrl = (plan.contactSalesUrl || '').trim();
                    const displayCredits = isYearly
                      ? (plan.creditsYearly || 0)
                      : (plan.creditsMonthly || plan.pagesPerMonth || 0);

                    return (
                      <FadeInCard
                        key={plan.id}
                        index={index}
                        className={`relative rounded-2xl ${
                          isPopular ? 'border-2 border-purple-500' : 'border border-gray-200'
                        } bg-white p-6 transition-shadow ${
                          isPopular ? 'hover:shadow-xl' : 'hover:shadow-lg hover:border-gray-300'
                        } flex flex-col h-full`}
                      >
                        {isPopular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <span className="bg-purple-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                              {t.mostPopular}
                            </span>
                          </div>
                        )}
                        <div className="mb-6">
                          <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
                          <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                        </div>
                        <div className="flex items-baseline gap-2 mb-4 flex-wrap">
                          {isContactSales ? (
                            <span className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
                              {plan.buttonText || t.contactTeam}
                            </span>
                          ) : (
                            <>
                              <span className="text-5xl font-bold text-gray-900">{formatPlanPriceUsd(price, lang)}</span>
                              {price > 0 && (
                                <>
                                  <span className="text-gray-500 text-lg">/{isYearly ? t.year : t.month}</span>
                                  {isYearly && plan.yearlyDiscount > 0 && (
                                    <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded-full">
                                      -{plan.yearlyDiscount}%
                                    </span>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>
                        {/* Credits badge */}
                        {displayCredits > 0 && (
                          <div className="bg-purple-50 rounded-lg px-3 py-2 mb-6">
                            <div className="flex items-center gap-2">
                              <Coins className="w-4 h-4 text-purple-600" />
                              <span className="text-sm font-semibold text-purple-700">
                                {/* Fixed locale: bare toLocaleString() renders "1,200" on the
                                    server but "1200" in es-* browsers → hydration mismatch. */}
                                {displayCredits.toLocaleString(lang === 'en' ? 'en-US' : lang === 'de' ? 'de-DE' : lang === 'fr' ? 'fr-FR' : lang === 'pt' ? 'pt-BR' : 'es-ES')} {t.creditsIncluded}
                              </span>
                              <span className="text-xs text-purple-500">
                                {isYearly ? t.perYear : t.perMonth}
                              </span>
                            </div>
                          </div>
                        )}
                        <ul className="space-y-4 text-sm text-gray-600 flex-1">
                          {plan.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-center gap-3">
                              <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                        <Button
                          asChild
                          variant={isPrimary ? 'default' : 'outline'}
                          className={`w-full h-11 rounded-lg mt-8 ${
                            isPrimary
                              ? 'bg-purple-600 hover:bg-purple-700 text-white'
                              : 'border-gray-200'
                          }`}
                        >
                          {isContactSales && salesUrl ? (
                            <a href={salesUrl} target="_blank" rel="noopener noreferrer">
                              {plan.buttonText}
                            </a>
                          ) : (
                            <Link href="/sign-up">
                              {plan.buttonText}
                            </Link>
                          )}
                        </Button>
                      </FadeInCard>
                    );
                  })}
                </div>

                {/* Dots indicator */}
                {allPlans.length > 3 && (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    {Array.from({ length: allPlans.length - 2 }).map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setPlanStartIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-colors cursor-pointer ${
                          planStartIndex === idx ? 'bg-purple-600' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

        <AnimationContainer delay={0.3}>
          <div className="flex items-center justify-center gap-6 mt-4">
            <div className="flex items-center gap-2 text-gray-600">
              <CreditCard className="w-5 h-5" />
              <span>{t.noCard}</span>
            </div>
          </div>
        </AnimationContainer>
      </div>
    </section>
  );
}
